import { useBatchSamples, useBatchUpload } from '@api/hooks/useBatch';
import { PageContainer } from '@components/PageContainer';
import { Skeleton } from '@components/skeleton/Skeleton';
import { toastError } from '@components/Toast/Toast';
import type { UploadedFile } from '@gurupay/gp-ui-library';
import {
	Button,
	DownloadIcon,
	FileUploader,
	FileUploaderView,
	FileUploadStatus,
	Typography,
} from '@gurupay/gp-ui-library';
import { useRejectedFilesNotify } from '@hooks/useRejectedFilesNotify.ts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ALLOWED_MIMES = ['.xml', '.csv'];

export const BatchUpload = () => {
	const { t } = useTranslation('', { keyPrefix: 'payments.batch' });
	const { t: tFileUpload } = useTranslation('', { keyPrefix: 'fileInput' });
	const { data: samples, isLoading: isSamplesLoading } = useBatchSamples();
	const [file, setFile] = useState<File | undefined>(undefined);
	const uploadBatch = useBatchUpload();
	const displayFile: UploadedFile | undefined = file && {
		id: file.name,
		name: file.name,
		size: file.size,
		status: uploadBatch.isPending
			? FileUploadStatus.UPLOADING
			: uploadBatch.isError
				? FileUploadStatus.ERROR
				: FileUploadStatus.SUCCESS,
	};
	const { handleReject } = useRejectedFilesNotify({ size: 2 });

	const handleFileSelect = (selectedFiles: File[]) => {
		setFile(selectedFiles[0]);
		uploadBatch.mutate(selectedFiles[0]);
	};

	const handleSaveSample = (url: string) => {
		if (!url.startsWith('https://')) {
			toastError(t('unauthorizedUrl'));
			return;
		}
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const handleRemoveFile = () => {
		setFile(undefined);
		uploadBatch.reset();
	};
	const disableSubmit = !file || uploadBatch.isPending || uploadBatch.isError;

	return (
		<PageContainer backUrl="/payments" title={t('title')}>
			<div className="flex flex-col items-center max-w-[500px] mx-auto">
				<FileUploader
					className="w-full"
					files={displayFile ? [displayFile] : []}
					fileTypes={ALLOWED_MIMES}
					hint={tFileUpload('fileInfoBatch')}
					isDisabled={uploadBatch.isPending}
					label={
						<p className="text-sm-medium">
							<span className="font-bold">{tFileUpload('actionDesktop')} </span>
							{tFileUpload('actionDesktopSmall')}
						</p>
					}
					maxFiles={1}
					maxSizeMB={2}
					onFileReject={handleReject}
					onFileRemove={handleRemoveFile}
					onFileSelect={handleFileSelect}
					view={FileUploaderView.NORMAL}
				/>
				<Button
					href={
						disableSubmit
							? undefined
							: `/operations/batch/${uploadBatch?.data![0].batchId}`
					}
					className="self-end mt-space-16"
					disabled={disableSubmit}
				>
					{t('continue')}
				</Button>
				`
				<div className="self-start mt-space-24 w-full">
					<Typography className="font-medium" level="small-text">
						{t('sampleText')}
					</Typography>
					{isSamplesLoading || !samples ? (
						<Skeleton className="h-8 w-40 mt-space-8" />
					) : (
						<div className="flex gap-space-8 mt-space-8">
							{samples.map((sample) => (
								<Button
									key={sample.type}
									className="flex-row-reverse uppercase"
									icon={<DownloadIcon />}
									onClick={() => handleSaveSample(sample.url)}
									size="sm"
									variant="outlined"
								>
									{sample.type}
								</Button>
							))}
						</div>
					)}
				</div>
			</div>
		</PageContainer>
	);
};
